import * as React from "react";
import { QrReader } from "react-qr-reader";
import { useNavigate } from "react-router-dom";
import { setCurrentStep, setsteps } from "../Stepper/stepperSlice";
import { useDispatch } from "react-redux";
import NavbarStepper from "../Stepper/NavBarStepper";
import { useParams } from "react-router-dom";
import {
  useGetUninstallJobCardQuery, useAddjobCardMutation, useAddChangeLogMutation,
} from "../../api/apiSlice";
import { useEffect } from "react";
import {
  showError,
} from "../../../Global/notifications/notificationsSlice";
import { skipToken } from "@reduxjs/toolkit/query/react";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import { useConnectionMonitor } from "../connectionsTest";
import ConnectionAlert from "../ConnectionAlert";
import { useStepperStateRestoration } from "../useStepperStateRestoration";
import { parseDeviceQr } from "../parseDeviceQr";
import { useRefusedScan } from "../useRefusedScan";

// Mirrors is_uninstallable in the installer backend (NEU-841): an install QE approved
// (COM), or a declined one QE marked REMOVE - don't proceed, take the device off (23/10).
// Any other declined install is redone and approved first. The backend already refuses
// anything else with a reason, which the error handler below shows as-is; this only stops
// the screen rejecting a card the backend just allowed.
const isUninstallable = (card) =>
  card.state === "COM" || (card.state === "REDO" && card.qe_decline_outcome === "REMOVE");

export default function QRScanUninstall() {
  // Stepper state restoration
  useStepperStateRestoration();

  // Connection monitoring
  const {
    showAlert,
    showWeakWarning,
    handleRetry,
    handleDismiss,
    handleWeakWarningDismiss,
    responseTime,
    avgResponseTime,
    connectionDetails
  } = useConnectionMonitor();

  const [
    addChangeLog,
    {
      isSuccess: addChangeLogSuccess,
      isError: isaddChangeLogError,
      error: addChangeLogError,
    },
  ] = useAddChangeLogMutation();
  
  const [qrCodeData, setQRCodeData] = React.useState(null);
  const [isProcessing, setIsProcessing] = React.useState(false);

  const scannedRef = React.useRef(false);
  const sentRef = React.useRef(false);
  const badQrToastRef = React.useRef(false);

  const refusedScan = useRefusedScan();

  // Only called when a scan is refused.
  const resetScan = () => {
    refusedScan.refuse(qrCodeData?.serial_number);
    scannedRef.current = false;
    sentRef.current = false;
    setQRCodeData(null);
    setIsProcessing(false);
  };
  const dispatch = useDispatch();
  let navigate = useNavigate();
  let { install_state } = useParams();
  useEffect(() => { dispatch(setCurrentStep(0)); }, []);


  const [
    addJobCard,
    {
      data: job_card_data,
      isSuccess: isAddJobCardSuccess,
      isError: isAddJobCardError,
      error: addJobCardError,
    },
  ] = useAddjobCardMutation();

 
  const {
    data: job_card,
    isSuccess: isGetJobCardSuccess,
    isError: isGetJobCardError,
    error: getSmartNodeError,
  } = useGetUninstallJobCardQuery(
    qrCodeData
      ? {
          serial_number: qrCodeData.serial_number,
          imsi_number: qrCodeData.imsi_number,
        }
      : skipToken
  );


  useEffect(() => {
    if (isGetJobCardError) {
      dispatch(showError(getSmartNodeError.data.error));
      resetScan();
    }
  }, [isGetJobCardError, job_card]);


 

  useEffect(() => {
    if (isGetJobCardSuccess && qrCodeData && !sentRef.current) {
      const { serial_number } = qrCodeData;
      if (isUninstallable(job_card) && install_state === "UN") {
        sentRef.current = true;
        addChangeLog({ body: { serial_number, change: "Started uninstall from field" } });
      } else if (isUninstallable(job_card) && install_state === "RE") {
        sentRef.current = true;
        // Nothing comes off until the replacement is submitted (NEU-924), so it's a start (P4).
        addChangeLog({ body: { serial_number, change: "Started replacement from field" } });
      } else {
        resetScan();
        dispatch(showError("This device has not been installed"));
      }
    }
  }, [isGetJobCardSuccess, qrCodeData]);

  useEffect(() => {
    if (addChangeLogSuccess && qrCodeData && !isProcessing) {
      const { serial_number, imsi_number } = qrCodeData;
    
      if (isUninstallable(job_card)) {
        const body = {
          address: job_card.address,
          // geyser_description, not geyser_name. geyser_name is a read-only alias on
          // JobCardSerializer, so the backend ignored it and every uninstall and
          // replacement card was created with a blank description. Same for the two
          // site fields below, which the backend reads but nothing was sending.
          geyser_description: job_card.geyser_description || "",
          address_description: job_card.address_description || "",
          erf_number: job_card.erf_number || "",
          property_type: job_card.property_type || "house",
          building_name: job_card.building_name || null,
          unit_number: job_card.unit_number || null,
          // Without these the new card has no pin on the installed-customer map, and
          // the next installer cannot see what was on the wall.
          latitude: job_card.latitude,
          longitude: job_card.longitude,
          customer_consent: job_card.customer_consent,
          breaker_control: job_card.breaker_control,
          solar_geyser: job_card.solar_geyser,
          solar_pv: job_card.solar_pv,
          ripple_replacement: job_card.ripple_replacement,
          ripple_bypass: job_card.ripple_bypass,
          heat_pump: job_card.heat_pump,
          timer_installed: job_card.timer_installed,
          aborted: false,
          state: "BEFWA",
          install_state: install_state,
          archived_serial_number: serial_number,
          archived_job_card_number: job_card.id,
          archived_imsi_number: imsi_number,
        };
        setIsProcessing(true);
        addJobCard({body:body})
        
      }
      else{
        setIsProcessing(false);
        dispatch(showError("This device has not been installed"));
      }
    } 
  }, [addChangeLogSuccess, qrCodeData]);

  // The old device stays on in DSM until the job is submitted (NEU-924, A). Switching it
  // off here, at the start, left it off when the job was aborted, and the next attempt
  // failed (N15).
  useEffect(() => {
    if (isAddJobCardSuccess) {
      const { serial_number, imsi_number } = qrCodeData;
      navigate(`/installer/${job_card_data.job_card_reference_id}/${serial_number}/${imsi_number}/work_area_before_install/`);
    }
  }, [isAddJobCardSuccess]);

  useEffect(() => {
    if (isaddChangeLogError) {
      dispatch(showError(addChangeLogError.data?.error || "Failed to log change"));
      resetScan();
    }
  }, [isaddChangeLogError]);

  useEffect(() => {
    if (isAddJobCardError) {
      dispatch(showError(addJobCardError.data?.error || addJobCardError.data?.error_message || "Failed to create job card"));
      resetScan();
    }
  }, [isAddJobCardError]);

  return (
    <>
      {/* Connection Alert */}
      {showAlert && (
        <ConnectionAlert
          onRetry={handleRetry}
          onClose={handleDismiss}
          connectionDetails={connectionDetails}
          position="top"
          alertType="error"
        />
      )}
      
      {/* Weak Connection Warning */}
      {showWeakWarning && (
        <ConnectionAlert
          onRetry={handleRetry}
          onClose={handleWeakWarningDismiss}
          connectionDetails={connectionDetails}
          position="top"
          alertType="warning"
          responseTime={responseTime || avgResponseTime}
        />
      )}
      
      <NavbarStepper />

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "calc(100vh - 100px)", backgroundColor: "#000", px: 2 }}>
        {/* Viewfinder */}
        <Box sx={{ position: "relative", width: "85vw", height: "85vw", mb: 3 }}>
          {/* Corner brackets */}
          <Box sx={{ position: "absolute", top: 0, left: 0, width: 32, height: 32, borderTop: "3px solid #FDB940", borderLeft: "3px solid #FDB940", borderTopLeftRadius: "4px", zIndex: 2, pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", top: 0, right: 0, width: 32, height: 32, borderTop: "3px solid #FDB940", borderRight: "3px solid #FDB940", borderTopRightRadius: "4px", zIndex: 2, pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", bottom: 0, left: 0, width: 32, height: 32, borderBottom: "3px solid #FDB940", borderLeft: "3px solid #FDB940", borderBottomLeftRadius: "4px", zIndex: 2, pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", bottom: 0, right: 0, width: 32, height: 32, borderBottom: "3px solid #FDB940", borderRight: "3px solid #FDB940", borderBottomRightRadius: "4px", zIndex: 2, pointerEvents: "none" }} />

          <Box sx={{ width: "100%", height: "100%", overflow: "hidden", "& section": { height: "100% !important", paddingTop: "0 !important" }, "& section > div": { paddingTop: "0 !important", height: "100% !important", position: "relative !important" }, "& video": { objectFit: "cover !important", position: "static !important", width: "100% !important", height: "100% !important" } }}>
            {!qrCodeData && (
              <QrReader
                constraints={{ facingMode: "environment" }}
                onResult={(result) => {
                  if (!result || scannedRef.current) return;
                  const parsed = parseDeviceQr(result.text);
                  if (!parsed) {
                    if (!badQrToastRef.current) {
                      badQrToastRef.current = true;
                      dispatch(showError("Not a Tjommie QR code"));
                      setTimeout(() => { badQrToastRef.current = false; }, 3000);
                    }
                    return;
                  }
                  if (refusedScan.ignore(parsed.serial_number)) return;
                  scannedRef.current = true;
                  setQRCodeData(parsed);
                }}
              />
            )}
          </Box>
        </Box>

        <Typography sx={{ color: "rgba(255,255,255,0.8)", textAlign: "center", fontSize: "0.95rem" }}>
          Scan QR Code of the device being uninstalled.
        </Typography>
      </Box>
    </>
  );
}

